import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { LedgerBoardsApi, BoardEarnObjectivesResponse } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";

/**
 * The objectives assigned to a board, as its earn section renders them.
 *
 * The section used to be a hand-authored list of cards on the page, which made
 * every card a second copy of what the objective and its reward already own —
 * the label, the blurb and the points number were typed again, and nothing
 * kept them in agreement. Assigning an objective to the board is now the whole
 * authoring step, and this is the read.
 *
 * Public: the section is public copy, so it loads for logged-out visitors too.
 * Signed in, it also flags the member's used-up claim cards, so the key
 * carries sign-in state and every claim invalidates `boardEarnObjectivesKey`.
 *
 * No polling. Assignments change when an operator saves, not while somebody is
 * looking at the page; standings poll because the numbers move on their own,
 * and this does not.
 */
export const boardEarnObjectivesKey = ["board-earn-objectives"];

export function useGetBoardEarnObjectives(ledgerBoardId: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [...boardEarnObjectivesKey, ledgerBoardId, Boolean(fullToken)],
        queryFn: async (): Promise<BoardEarnObjectivesResponse> =>
            new LedgerBoardsApi(getConfig(fullToken)).getLedgerBoardsBoardIdEarnObjectives({
                boardId: ledgerBoardId!,
            }),
        enabled: !!ledgerBoardId,
        staleTime: 5 * 60_000,
    });
}
